import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiFileText,
  FiUsers,
  FiBriefcase,
  FiDollarSign,
  FiPlus,
  FiFolder,
  FiUserPlus,
  FiInfo,
  FiExternalLink,
  FiCalendar,
  FiClock,
  FiArrowRight,
  FiTrendingUp,
  FiGlobe,
  FiShield,
  FiAnchor,
  FiDatabase,
  FiSearch,
  FiGrid,
  FiRefreshCw,
  FiActivity,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { documentAPI, contactAPI, companyAPI } from '../../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr) => {
  if (!dateStr) return '--.--.----';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

// ---------------------------------------------------------------------------
// Animated counter hook
// ---------------------------------------------------------------------------

const useAnimatedCounter = (end, duration = 1400) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (end === 0) {
      setCount(0);
      return;
    }
    let start = 0;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);

  return count;
};

// ---------------------------------------------------------------------------
// Colour tokens
// ---------------------------------------------------------------------------

const COLORS = {
  primary: '#059669',
  primaryHover: '#047857',
  dark: '#064e3b',
  darker: '#022c22',
  light: '#ecfdf5',
  lightAlt: '#d1fae5',
  accent: '#10b981',
  accentGlow: 'rgba(16, 185, 129, 0.35)',
  white: '#ffffff',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: 'rgba(5, 150, 105, 0.15)',
  cardBg: 'rgba(255, 255, 255, 0.72)',
  glassBorder: 'rgba(255, 255, 255, 0.45)',
  shadow: 'rgba(5, 150, 105, 0.08)',
  shadowHover: 'rgba(5, 150, 105, 0.18)',
};

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 260, damping: 24 },
  },
};

const tableRowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.07, type: 'spring', stiffness: 200, damping: 20 },
  }),
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ icon: Icon, label, value, color, index }) {
  const animatedValue = useAnimatedCounter(value);
  const gradient = `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`;

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -6, scale: 1.03, boxShadow: `0 20px 50px ${COLORS.shadowHover}` }}
      whileTap={{ scale: 0.98 }}
      style={{
        ...styles.statCard,
        background: gradient,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={styles.statCardInner}>
        <div>
          <p style={styles.statLabel}>{label}</p>
          <motion.h2
            style={{ ...styles.statValue, color }}
            key={value}
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            {animatedValue}
          </motion.h2>
        </div>
        <motion.div
          style={{
            ...styles.statIconWrap,
            background: `${color}15`,
            color,
          }}
          whileHover={{ rotate: 12 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <Icon size={28} />
        </motion.div>
      </div>
      {/* decorative bar */}
      <motion.div
        style={{ ...styles.statBar, background: color }}
        initial={{ width: 0 }}
        animate={{ width: '60%' }}
        transition={{ delay: 0.4 + index * 0.12, duration: 0.8, ease: 'easeOut' }}
      />
    </motion.div>
  );
}

function QuickActionButton({ icon: Icon, label, to, delay }) {
  return (
    <motion.div variants={itemVariants} whileHover={{ y: -4, scale: 1.04 }} whileTap={{ scale: 0.96 }}>
      <Link to={to} style={styles.quickActionLink}>
        <div style={styles.quickActionCard}>
          <motion.div
            style={styles.quickActionIcon}
            whileHover={{ rotate: -8, scale: 1.15 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <Icon size={24} />
          </motion.div>
          <span style={styles.quickActionLabel}>{label}</span>
          <FiArrowRight size={16} style={{ color: COLORS.textMuted, marginTop: 4 }} />
        </div>
      </Link>
    </motion.div>
  );
}

function ReferenceLink({ href, label, icon: Icon }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.refLink}
      whileHover={{
        y: -3,
        scale: 1.04,
        boxShadow: `0 8px 30px ${COLORS.shadowHover}`,
        background: COLORS.light,
      }}
      whileTap={{ scale: 0.97 }}
    >
      {Icon ? <Icon size={16} style={{ color: COLORS.primary, flexShrink: 0 }} /> : <FiExternalLink size={16} style={{ color: COLORS.primary, flexShrink: 0 }} />}
      <span style={styles.refLinkText}>{label}</span>
    </motion.a>
  );
}

// ---------------------------------------------------------------------------
// Reference links data
// ---------------------------------------------------------------------------

const REFERENCE_LINKS = [
  { label: 'ICEGATE', href: 'https://www.icegate.gov.in', icon: FiShield },
  { label: 'DGFT', href: 'https://www.dgft.gov.in', icon: FiGlobe },
  { label: 'Dollar Rates', href: 'https://www.xe.com', icon: FiDollarSign },
  { label: 'Export Import Policy', href: 'https://www.dgft.gov.in', icon: FiFileText },
  { label: 'IEC / e-BRC', href: 'https://www.dgft.gov.in/CP/', icon: FiCheckCircle },
  { label: 'FEDAI', href: 'https://www.fedai.org.in', icon: FiTrendingUp },
  { label: 'ECGC', href: 'https://www.ecgc.in', icon: FiShield },
  { label: 'HS Codes', href: 'https://www.cbic.gov.in/htdocs-cbec/customs/cst2004/csnt-main', icon: FiSearch },
  { label: 'CBEC', href: 'https://www.cbic.gov.in', icon: FiDatabase },
  { label: 'PAN Verification', href: 'https://www.incometax.gov.in/iec/foportal/', icon: FiSearch },
  { label: 'Swift Codes', href: 'https://www.swift.com', icon: FiGrid },
  { label: 'MCA', href: 'https://www.mca.gov.in', icon: FiBriefcase },
  { label: 'DIPP', href: 'https://dpiit.gov.in', icon: FiActivity },
  { label: 'Plant Quarantine', href: 'https://plantquarantineindia.nic.in', icon: FiAlertCircle },
  { label: 'ACU', href: 'https://www.asianclearingunion.org', icon: FiAnchor },
  { label: 'AD Banks', href: 'https://www.rbi.org.in', icon: FiBriefcase },
];

// ---------------------------------------------------------------------------
// Dashboard Component
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const { user, activeCompanyId, activeCompany } = useAuth();

  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalContacts: 0,
    activeCompanies: 0,
    totalValue: 0,
  });
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ---- Fetch data --------------------------------------------------------

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [docsRes, contactsRes, companiesRes] = await Promise.allSettled([
        documentAPI.getAll(),
        contactAPI.getAll(),
        companyAPI.getAll(),
      ]);

      const documents = docsRes.status === 'fulfilled' ? docsRes.value?.data || docsRes.value || [] : [];
      const contacts = contactsRes.status === 'fulfilled' ? contactsRes.value?.data || contactsRes.value || [] : [];
      const companies = companiesRes.status === 'fulfilled' ? companiesRes.value?.data || companiesRes.value || [] : [];

      const docList = Array.isArray(documents) ? documents : [];
      const contactList = Array.isArray(contacts) ? contacts : [];
      const companyList = Array.isArray(companies) ? companies : [];

      const totalValue = docList.reduce((sum, d) => sum + (parseFloat(d.total_usd) || 0), 0);

      setStats({
        totalDocuments: docList.length,
        totalContacts: contactList.length,
        activeCompanies: companyList.filter((c) => c.is_active !== false).length || companyList.length,
        totalValue: Math.round(totalValue),
      });

      const sorted = [...docList]
        .sort((a, b) => new Date(b.created_at || b.document_date) - new Date(a.created_at || a.document_date))
        .slice(0, 5);
      setRecentDocuments(sorted);
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ---- Stat card config ---------------------------------------------------

  const statCards = [
    { icon: FiFileText, label: 'Total Documents', value: stats.totalDocuments, color: '#059669' },
    { icon: FiUsers, label: 'Total Contacts', value: stats.totalContacts, color: '#0891b2' },
    { icon: FiBriefcase, label: 'Active Companies', value: stats.activeCompanies, color: '#7c3aed' },
    { icon: FiDollarSign, label: 'Total Value (USD)', value: stats.totalValue, color: '#ea580c' },
  ];

  // ---- Render -------------------------------------------------------------

  return (
    <div style={styles.pageWrapper}>
      {/* Decorative blurred blobs for parallax feel */}
      <div style={styles.blobOne} />
      <div style={styles.blobTwo} />
      <div style={styles.blobThree} />

      <motion.div
        style={styles.container}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* --------- Header / Welcome --------- */}
        <motion.div variants={itemVariants} style={styles.headerSection}>
          <div style={styles.headerLeft}>
            <motion.h1
              style={styles.welcomeTitle}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              Welcome back,{' '}
              <span style={styles.userName}>
                {user?.name || user?.username || 'User'}
              </span>
            </motion.h1>
            <motion.p
              style={styles.welcomeSub}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
            >
              <FiBriefcase size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {activeCompany?.exporter_name || 'Export Management System'}
              <span style={styles.dateBadge}>
                <FiCalendar size={12} style={{ marginRight: 4 }} />
                {formatDate(new Date().toISOString())}
              </span>
            </motion.p>
          </div>

          <motion.button
            style={styles.refreshBtn}
            whileHover={{ scale: 1.08, boxShadow: `0 4px 20px ${COLORS.accentGlow}` }}
            whileTap={{ scale: 0.92, rotate: 180 }}
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            title="Refresh data"
          >
            <motion.div
              animate={refreshing ? { rotate: 360 } : { rotate: 0 }}
              transition={refreshing ? { repeat: Infinity, duration: 1, ease: 'linear' } : {}}
            >
              <FiRefreshCw size={18} />
            </motion.div>
          </motion.button>
        </motion.div>

        {/* --------- Stat Cards --------- */}
        <motion.div style={styles.statsGrid} variants={containerVariants}>
          {statCards.map((card, idx) => (
            <StatCard key={card.label} {...card} index={idx} />
          ))}
        </motion.div>

        {/* --------- Middle Row: Recent Docs + Quick Actions --------- */}
        <div style={styles.middleRow}>
          {/* Recent Documents */}
          <motion.div variants={itemVariants} style={{ ...styles.glassCard, flex: 2, minWidth: 0 }}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitleWrap}>
                <FiClock size={20} style={{ color: COLORS.primary }} />
                <h3 style={styles.sectionTitle}>Recent Documents</h3>
              </div>
              <Link to="/commercial-invoices" style={styles.viewAllLink}>
                View All <FiArrowRight size={14} style={{ marginLeft: 4 }} />
              </Link>
            </div>

            <div style={styles.tableWrapper}>
              <AnimatePresence>
                {loading ? (
                  <motion.div
                    style={styles.loadingState}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
                    >
                      <FiRefreshCw size={28} style={{ color: COLORS.primary }} />
                    </motion.div>
                    <p style={{ color: COLORS.textSecondary, marginTop: 12 }}>
                      Loading documents...
                    </p>
                  </motion.div>
                ) : recentDocuments.length === 0 ? (
                  <motion.div
                    style={styles.emptyState}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <FiFileText size={40} style={{ color: COLORS.textMuted, marginBottom: 12 }} />
                    <p style={{ color: COLORS.textSecondary }}>No documents found</p>
                    <Link to="/commercial-invoices/new" style={styles.emptyActionBtn}>
                      <FiPlus size={14} /> Create your first document
                    </Link>
                  </motion.div>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Invoice No</th>
                        <th style={styles.th}>Buyer</th>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentDocuments.map((doc, i) => (
                        <motion.tr
                          key={doc.id || i}
                          custom={i}
                          variants={tableRowVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover={{ backgroundColor: COLORS.light }}
                          style={styles.tr}
                        >
                          <td style={styles.td}>
                            <Link
                              to={`/commercial-invoices/edit/${doc.id}`}
                              style={styles.docLink}
                            >
                              {doc.invoice_no || `DOC-${doc.id}`}
                            </Link>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.typeBadge}>
                              {doc.buyer_name || '--'}
                            </span>
                          </td>
                          <td style={{ ...styles.td, color: COLORS.textSecondary, fontSize: 13 }}>
                            {formatDate(doc.document_date)}
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                ...(doc.status === 'finalized'
                                  ? styles.statusCompleted
                                  : doc.status === 'saved'
                                  ? styles.statusPending
                                  : styles.statusDraft),
                              }}
                            >
                              {doc.status || 'draft'}
                            </span>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div variants={itemVariants} style={{ ...styles.glassCard, flex: 1, minWidth: 260 }}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionTitleWrap}>
                <FiGrid size={20} style={{ color: COLORS.primary }} />
                <h3 style={styles.sectionTitle}>Quick Actions</h3>
              </div>
            </div>
            <motion.div style={styles.quickActionsGrid} variants={containerVariants}>
              <QuickActionButton icon={FiPlus} label="New Document" to="/commercial-invoices/new" />
              <QuickActionButton icon={FiFolder} label="Open Documents" to="/commercial-invoices" />
              <QuickActionButton icon={FiUserPlus} label="Contacts" to="/contacts" />
              <QuickActionButton icon={FiInfo} label="Company Info" to="/companies" />
            </motion.div>
          </motion.div>
        </div>

        {/* --------- External Reference Links --------- */}
        <motion.div variants={itemVariants} style={styles.glassCard}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitleWrap}>
              <FiGlobe size={20} style={{ color: COLORS.primary }} />
              <h3 style={styles.sectionTitle}>External Reference Links</h3>
            </div>
            <span style={styles.refSubtitle}>Indian Export Regulatory Resources</span>
          </div>
          <motion.div style={styles.refGrid} variants={containerVariants}>
            {REFERENCE_LINKS.map((link) => (
              <motion.div key={link.label} variants={itemVariants}>
                <ReferenceLink {...link} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* --------- Footer --------- */}
        <motion.div variants={itemVariants} style={styles.footer}>
          <p style={styles.footerText}>
            Export Management System &mdash; Streamlining International Trade
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
  /* Page-level */
  pageWrapper: {
    position: 'relative',
    minHeight: '100vh',
    background: `linear-gradient(165deg, ${COLORS.light} 0%, #f0fdf8 35%, #ffffff 65%, ${COLORS.lightAlt} 100%)`,
    overflow: 'hidden',
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
  },

  /* Decorative blobs */
  blobOne: {
    position: 'fixed',
    top: '-10%',
    right: '-8%',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${COLORS.primary}10 0%, transparent 70%)`,
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  blobTwo: {
    position: 'fixed',
    bottom: '5%',
    left: '-6%',
    width: 420,
    height: 420,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${COLORS.accent}12 0%, transparent 70%)`,
    filter: 'blur(50px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  blobThree: {
    position: 'fixed',
    top: '40%',
    left: '50%',
    width: 320,
    height: 320,
    borderRadius: '50%',
    background: `radial-gradient(circle, #7c3aed08 0%, transparent 70%)`,
    filter: 'blur(40px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  /* Container */
  container: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 1320,
    margin: '0 auto',
    padding: '32px 28px 48px',
  },

  /* Header */
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32,
    flexWrap: 'wrap',
    gap: 16,
  },
  headerLeft: {},
  welcomeTitle: {
    fontSize: 30,
    fontWeight: 800,
    color: COLORS.textPrimary,
    margin: 0,
    letterSpacing: '-0.5px',
    lineHeight: 1.25,
  },
  userName: {
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  welcomeSub: {
    marginTop: 8,
    fontSize: 14,
    color: COLORS.textSecondary,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  dateBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 20,
    background: COLORS.light,
    color: COLORS.primary,
    border: `1px solid ${COLORS.border}`,
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.cardBg,
    backdropFilter: 'blur(12px)',
    color: COLORS.primary,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s',
  },

  /* Stats grid */
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 20,
    marginBottom: 28,
  },
  statCard: {
    position: 'relative',
    borderRadius: 16,
    padding: '22px 24px 18px',
    backdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.glassBorder}`,
    boxShadow: `0 4px 24px ${COLORS.shadow}`,
    overflow: 'hidden',
    cursor: 'default',
    transition: 'box-shadow 0.3s',
  },
  statCardInner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    margin: '0 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  statValue: {
    fontSize: 34,
    fontWeight: 800,
    margin: 0,
    letterSpacing: '-1px',
  },
  statIconWrap: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 3,
    borderRadius: '0 4px 4px 0',
    opacity: 0.6,
  },

  /* Middle row */
  middleRow: {
    display: 'flex',
    gap: 24,
    marginBottom: 28,
    flexWrap: 'wrap',
  },

  /* Glass card */
  glassCard: {
    background: COLORS.cardBg,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 18,
    border: `1px solid ${COLORS.glassBorder}`,
    boxShadow: `0 4px 30px ${COLORS.shadow}`,
    padding: '24px 28px',
    marginBottom: 0,
  },

  /* Section header */
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 8,
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: 0,
  },
  viewAllLink: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.primary,
    textDecoration: 'none',
    padding: '5px 12px',
    borderRadius: 8,
    background: COLORS.light,
    transition: 'all 0.2s',
  },

  /* Table */
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 4px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    borderBottom: `2px solid ${COLORS.border}`,
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.15s',
    borderRadius: 10,
  },
  td: {
    padding: '12px 14px',
    fontSize: 14,
    color: COLORS.textPrimary,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  docLink: {
    color: COLORS.primary,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'color 0.15s',
  },
  typeBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    background: `${COLORS.primary}10`,
    color: COLORS.primary,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 20,
    textTransform: 'capitalize',
  },
  statusCompleted: {
    background: '#dcfce7',
    color: '#15803d',
  },
  statusPending: {
    background: '#fef3c7',
    color: '#b45309',
  },
  statusDraft: {
    background: '#f1f5f9',
    color: '#64748b',
  },

  /* Loading / empty */
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 0',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 0',
  },
  emptyActionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    padding: '8px 18px',
    borderRadius: 10,
    background: COLORS.primary,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'background 0.2s',
  },

  /* Quick actions */
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  quickActionLink: {
    textDecoration: 'none',
  },
  quickActionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 12px 16px',
    borderRadius: 14,
    background: `linear-gradient(160deg, ${COLORS.light} 0%, #ffffff 100%)`,
    border: `1px solid ${COLORS.border}`,
    boxShadow: `0 2px 12px ${COLORS.shadow}`,
    transition: 'all 0.25s',
    cursor: 'pointer',
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.accent})`,
    color: '#fff',
    marginBottom: 10,
    boxShadow: `0 4px 16px ${COLORS.accentGlow}`,
  },
  quickActionLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },

  /* Reference links */
  refSubtitle: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textMuted,
    letterSpacing: '0.3px',
  },
  refGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 10,
  },
  refLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 10,
    background: '#ffffff',
    border: `1px solid ${COLORS.border}`,
    textDecoration: 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  refLinkText: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  /* Footer */
  footer: {
    textAlign: 'center',
    marginTop: 32,
    paddingTop: 20,
    borderTop: `1px solid ${COLORS.border}`,
  },
  footerText: {
    fontSize: 13,
    color: COLORS.textMuted,
    margin: 0,
  },
};
